import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import FormControl from '@mui/material/FormControl'
import Highcharts, {
  Options as ChartOptions,
  PointOptionsObject,
} from 'highcharts'
import MenuItem from '@mui/material/MenuItem'
import HighchartsReact from 'highcharts-react-official'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import styled from '@emotion/styled'
import { useParams } from 'react-router-dom'

//@ts-ignore
import gStyles from '../../global.module.css'
import { FormatPrice as formatPrice } from '../../helpers/FormatPrice'

import CumTable from './CumTable'
//@ts-ignore
import styles from './CumComp.module.css'

export type CumCompProps = c.r<{}>

export default React.memo<CumCompProps>(function CumComp() {
  //#region global
  const { customerID } = useParams<'customerID'>()
  if (!customerID) {
    throw new ReferenceError(
      `No customerID inside <CumComp />. Contact dev dpt, screenshot this and contact Dev Dpt`,
    )
  }
  const [solarCompany] = r.useCustomerField(customerID, 'solarCompany')
  //#endregion global
  //#region styling
  const StyledButton = React.useMemo(() => {
    return styled.button`
      background-color: ${c.companyColors[solarCompany]};
      border-radius: 10px;
      border: none;
      color: white;
      cursor: pointer;
      font-size: 20px;
      font-weight: bold;
      padding: 7px;
    `
  }, [solarCompany])
  //#endregion styling
  //#region local
  // const [years, setYears] = React.useState(25)

  const [cashStr, , handleCashStr] = r.useInputVal('')
  const [powerStr, , handlePowerStr] = r.useInputVal('')
  const [percentStr, , handlePercentStr] = r.useInputVal('6')
  const [loanPaymentStr, , handleLoanPaymentStr] = r.useInputVal('')
  const [loanLenStr, , handleLoanLenStr] = r.useInputVal('10')
  const [ppaStr, , handlePPAStr] = r.useInputVal('')
  const [ppaPercentStr, , handlePPAPercentStr] = r.useInputVal('2.9')

  const [showingCashPlusPower, setShowingCashPlusPower] = React.useState(false)
  const [showingLoanPlusPower, setShowingLoanPlusPower] = React.useState(false)
  const [showingPPA, setShowingPPA] = React.useState(false)

  const powerYearlyTotals = React.useMemo((): c.numbers => {
    if (!powerStr || !percentStr!) return c.EMPTY_ARRAY
    const power = c.parseFloating(powerStr)
    const percent = c.parseFloating(percentStr)
    let res: number[] = [power * 12]

    for (const i of c.range(YEARS - 1, 1)) {
      const prevYear = res[i - 1]!

      res[i] = Math.round(prevYear + prevYear * (percent / 100))
    }

    return res
  }, [percentStr, powerStr])

  const cashPlusPower = React.useMemo((): c.numbers => {
    if (powerYearlyTotals === c.EMPTY_ARRAY) return c.EMPTY_ARRAY
    if (!cashStr) return c.EMPTY_ARRAY

    const cash = c.parseFloating(cashStr)

    let res: number[] = [cash + powerYearlyTotals[0]!]

    for (const i of c.range(YEARS - 1, 1)) {
      const prevYear = res[i - 1]!

      res[i] = Math.round(prevYear + powerYearlyTotals[i]!)
    }

    return res
  }, [cashStr, powerYearlyTotals])
  const cashPlusPowerPoints = React.useMemo(
    (): PointOptionsObject[] =>
      cashPlusPower.map((cpp, i) => ({
        y: cpp,
        x: (i + 1) * xWidth,
        marker: { enabled: setMarker(i * xWidth) },
      })),
    [cashPlusPower],
  )

  const loanPlusPower = React.useMemo((): c.numbers => {
    if (powerYearlyTotals === c.EMPTY_ARRAY) return c.EMPTY_ARRAY
    if (!loanPaymentStr) return c.EMPTY_ARRAY

    const loanPayment = c.parseFloating(loanPaymentStr)

    const loanLen = c.parseFloating(loanLenStr)

    let res: number[] = [loanPayment * 12 + powerYearlyTotals[0]!]

    for (const i of c.range(YEARS - 1, 1)) {
      const prev = res[i - 1]!
      const loanPaymentThisYear = i < loanLen ? loanPayment * 12 : 0
      const curr = loanPaymentThisYear + powerYearlyTotals[i]!
      res[i] = Math.round(prev + curr)
    }

    return res
  }, [loanLenStr, loanPaymentStr, powerYearlyTotals])
  const loanPlusPowerPoints = React.useMemo(
    (): PointOptionsObject[] =>
      loanPlusPower.map((lpp, i) => ({
        marker: { enabled: setMarker(i * xWidth) },
        x: (i + 1) * xWidth,
        y: lpp,
      })),
    [loanPlusPower],
  )

  const ppaMonthlyPaymentsByYear = React.useMemo((): c.numbers => {
    if (!ppaStr) return c.EMPTY_ARRAY
    if (!ppaPercentStr) return c.EMPTY_ARRAY

    const ppa = c.parseFloating(ppaStr)
    const ppaPercent = Number.parseFloat(ppaPercentStr)

    let res: number[] = [ppa]

    for (const i of c.range(YEARS - 1, 1)) {
      const prev = res[i - 1]!
      res[i] = Math.round(prev + prev * (ppaPercent / 100))
    }

    return res
  }, [ppaPercentStr, ppaStr])
  const ppaYearlyTotals = React.useMemo((): c.numbers => {
    if (ppaMonthlyPaymentsByYear === c.EMPTY_ARRAY) return c.EMPTY_ARRAY

    let res: number[] = [ppaMonthlyPaymentsByYear[0]! * 12]

    for (const i of c.range(ppaYears - 1, 1)) {
      const prevYear = res[i - 1]!
      const curr = ppaMonthlyPaymentsByYear[i]! * 12

      res[i] = Math.round(prevYear + curr)
    }

    return res
  }, [ppaMonthlyPaymentsByYear])
  const ppaYearlyPoints = React.useMemo(
    (): PointOptionsObject[] =>
      ppaYearlyTotals
        .map((ppy, i) => ({
          marker: { enabled: setMarker(i * xWidth) },
          x: (i + 1) * xWidth,
          y: ppy,
        }))
        .concat(
          c.range(5).map((_, i) => ({
            marker: { enabled: false },
            x: (ppaYears + i) * xWidth,
            y: 0,
          })),
        ),
    [ppaYearlyTotals],
  )

  const chartConfig = React.useMemo(
    (): ChartOptions => ({
      accessibility: { enabled: false },
      title: { text: '' },
      chart: { type: 'spline', width: 600 },
      credits: { enabled: false },
      series: [
        {
          color: '#B186F1',
          data: cashPlusPowerPoints,
          name: 'Cash+Electricity',
          showInLegend: true,
          type: 'line',
          visible: showingCashPlusPower,
        },
        {
          color: '#2B8A3E',
          data: loanPlusPowerPoints,
          name: 'Finance+Electricity',
          showInLegend: true,
          type: 'line',
          visible: showingLoanPlusPower,
        },
        {
          color: '#23368D',
          data: ppaYearlyPoints,
          name: 'PPA',
          showInLegend: true,
          type: 'line',
          visible: showingPPA,
        },
      ],
      tooltip: {
        formatter: function () {
          //@ts-ignore
          return `Year: <b>${parseInt(this.x!)}</b>
          </br>
          Electricity bill: <b>${formatPrice(
            Math.round(
              powerYearlyTotals[c.parseFloating(this.x!) - 1]! / xWidth,
            ),
            '$',
          )}</b>
          <br/>
          Cash+Electricity: <b>${formatPrice(
            Math.round(cashPlusPower[c.parseFloating(this.x!) - 1]!),
            '$',
          )}</b>
          <br/>
          Finance+Electricity: <b>${formatPrice(
            Math.round(loanPlusPower[c.parseFloating(this.x!) - 1]!),
            '$',
          )}</b>
         ${
           c.parseFloating(this.x!) <= 25
             ? ` <br/>
             PPA: <b>${formatPrice(
               Math.round(ppaYearlyTotals[c.parseFloating(this.x!) - 1]!),
               '$',
             )}</b>`
             : ''
         }
          `
        },
      },
      xAxis: {
        categories: xLabels,
        tickInterval: 60,
        title: { text: 'Years', textAlign: 'center' },
      },
      yAxis: {
        crosshair: true,
        tickPixelInterval: 60,
        title: { text: 'USD $' },
      },
    }),
    [
      cashPlusPower,
      cashPlusPowerPoints,
      loanPlusPower,
      loanPlusPowerPoints,
      powerYearlyTotals,
      ppaYearlyPoints,
      ppaYearlyTotals,
      showingCashPlusPower,
      showingLoanPlusPower,
      showingPPA,
    ],
  )
  //#endregion local

  const activateCashPlusPower = React.useCallback(() => {
    if (
      powerYearlyTotals === c.EMPTY_ARRAY ||
      cashPlusPower === c.EMPTY_ARRAY
    ) {
      // toast
      return
    }
    setShowingCashPlusPower(true)
  }, [cashPlusPower, powerYearlyTotals])
  const activateLoanPlusPower = React.useCallback(() => {
    if (
      powerYearlyTotals === c.EMPTY_ARRAY ||
      loanPlusPower === c.EMPTY_ARRAY
    ) {
      // toast
      return
    }
    setShowingLoanPlusPower(true)
  }, [loanPlusPower, powerYearlyTotals])
  const activatePPA = React.useCallback(() => {
    if (ppaYearlyTotals === c.EMPTY_ARRAY) {
      // toast
      return
    }
    setShowingPPA(true)
  }, [ppaYearlyTotals])

  return (
    <div className={styles['container']}>
      <div className={gStyles['row-justify-between-100']}>
        <div className={styles['input-group']}>
          <TextField
            label="Cash"
            onChange={handleCashStr}
            type="number"
            value={cashStr}
          />

          <TextField
            label="Electricity Bill"
            onChange={handlePowerStr}
            type="number"
            value={powerStr}
          />

          <TextField
            label="Percent Increase"
            onChange={handlePercentStr}
            type="number"
            value={percentStr}
          />

          <StyledButton onClick={activateCashPlusPower}>
            Show Cash+Electricity
          </StyledButton>
        </div>

        <div className={styles['input-group']}>
          <TextField
            label="Loan Payment"
            onChange={handleLoanPaymentStr}
            type="number"
            value={loanPaymentStr}
          />

          <FormControl>
            <InputLabel id="cum-comp-loan-len">{yearsSelectLabel}</InputLabel>
            <Select
              defaultValue="10"
              label={yearsSelectLabel}
              labelId="cum-comp-loan-len"
              onChange={handleLoanLenStr}
              value={loanLenStr}
            >
              <MenuItem value="2">2</MenuItem>
              <MenuItem value="3">3</MenuItem>
              <MenuItem value="4">4</MenuItem>
              <MenuItem value="5">5</MenuItem>
              <MenuItem value="6">6</MenuItem>
              <MenuItem value="7">7</MenuItem>
              <MenuItem value="10">10</MenuItem>
              <MenuItem value="12">12</MenuItem>
              <MenuItem value="15">15</MenuItem>
              <MenuItem value="20">20</MenuItem>
              <MenuItem value="25">25</MenuItem>
            </Select>
          </FormControl>

          <StyledButton onClick={activateLoanPlusPower}>
            Show Finance+Electricity
          </StyledButton>
        </div>

        {c.CODENAME !== 'nirvana' && (
          <div className={styles['input-group']}>
            <TextField
              label="PPA"
              onChange={handlePPAStr}
              type="number"
              value={ppaStr}
            />

            <TextField
              label="PPA %"
              onChange={handlePPAPercentStr}
              type="number"
              value={ppaPercentStr}
            />

            <StyledButton onClick={activatePPA}>Show PPA</StyledButton>
          </div>
        )}
      </div>

      <HighchartsReact highcharts={Highcharts} options={chartConfig} />

      <CumTable
        cashPlusPower={cashPlusPower}
        customerID={customerID}
        loanPlusPower={loanPlusPower}
        ppaYearlyTotals={ppaYearlyTotals}
      />
    </div>
  )
})

const YEARS = 30
const ppaYears = 25
const xWidth = 12

const setMarker = (index: number): boolean => {
  if (
    index / xWidth === 0 ||
    index / xWidth === 4 ||
    index / xWidth === 9 ||
    index / xWidth === 14 ||
    index / xWidth === 19 ||
    index / xWidth === 24 ||
    index / xWidth === 29
  ) {
    return true
  } else {
    return false
  }
}

const xLabels = new Array(361).fill(0).map((_, i) => {
  if (setMarker(i)) {
    return (i / xWidth).toString()
  } else {
    return (i / xWidth).toString()
  }
})

const yearsSelectLabel = 'Years'
